import React, { FC } from "react";

interface ModifierItem {
  id: number;
  modifierItem: {
    id: number;
    name: string;
    onlinePrice: number;
  };
  orderBy: number;
}

interface ModifierSet {
  id: number;
  name: string;
  noOfRequired: number;
  maximum: number;
  modifierSetModifierItems: ModifierItem[];
}

interface ModifierSectionProps {
  modifierSet: ModifierSet;
  selectedState: Record<number, boolean>;
  onChange: (modifierSetId: number, itemId: number, checked: boolean) => void;
  showError: boolean;
}

const ModifierSection: FC<ModifierSectionProps> = ({
  modifierSet,
  selectedState,
  onChange,
  showError,
}) => {
  const selectedCount = Object.values(selectedState).filter(Boolean).length;
  const isRequired = modifierSet.noOfRequired > 0;
  const hasMinError = showError && selectedCount < modifierSet.noOfRequired;
  const hasMaxError =
    showError && modifierSet.maximum > 0 && selectedCount > modifierSet.maximum;

  const sortedItems = [...(modifierSet.modifierSetModifierItems || [])].sort(
    (a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0)
  );

  return (
    <div
      className={`py-3 transition-colors ${
        (hasMinError || hasMaxError) ? "bg-red-50/50 dark:bg-red-900/10 ring-1 ring-red-200 dark:ring-red-800 rounded-lg mx-2" : ""
      }`}
      data-modifier-set-id={modifierSet.id}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 px-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {modifierSet.name}
          </h4>
          {isRequired && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              Required
            </span>
          )}
          {!isRequired && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              Optional
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {isRequired
            ? `Select ${modifierSet.noOfRequired}${
                modifierSet.maximum > modifierSet.noOfRequired
                  ? ` - ${modifierSet.maximum}`
                  : ""
              }`
            : `Up to ${modifierSet.maximum}`}
        </span>
      </div>

      {/* Error messages */}
      {hasMinError && (
        <p className="text-xs text-red-500 dark:text-red-400 px-4 mb-2">
          Please select at least {modifierSet.noOfRequired} option
          {modifierSet.noOfRequired > 1 ? "s" : ""}.
        </p>
      )}
      {hasMaxError && (
        <p className="text-xs text-red-500 dark:text-red-400 px-4 mb-2">
          Maximum {modifierSet.maximum} selections allowed.
        </p>
      )}

      {/* Modifier items */}
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg mx-4">
        {sortedItems.map((item, index) => {
          const isChecked = !!selectedState[item.id];
          const isDisabled =
            !isChecked &&
            modifierSet.maximum > 0 &&
            selectedCount >= modifierSet.maximum;

          return (
            <label
              key={item.id}
              className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                isDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-slate-100 dark:hover:bg-slate-700/50"
              } ${
                index < sortedItems.length - 1
                  ? "border-b border-slate-100 dark:border-slate-700/50"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    if (!isDisabled) {
                      onChange(modifierSet.id, item.id, !isChecked);
                    }
                  }}
                  className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-primary-500 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400 dark:bg-slate-700"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {item.modifierItem.name}
                </span>
              </div>
              {item.modifierItem.onlinePrice > 0 && (
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  +${item.modifierItem.onlinePrice.toFixed(2)}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default ModifierSection;
